import React, { useEffect, useRef, useState } from 'react';
import AgentMark from './AgentMark.jsx';
import { AGENTS } from '../agents.js';
import { toAnswerPayload } from '../desk-model.js';

// AskCard — the core answer-in-place pattern, ported from the prototype.
// Renders four shapes (fork / review / pick / unblock), the "+ Why it matters"
// toggle, an "Open the thread" foot, and an answered/undo state with a short
// optimistic undo grace window.
//
// props: { ask /* view-model */, onAnswer, onUndo, onOpenThread, compact }
//   onAnswer(turnId, payload)  — payload built via toAnswerPayload(ask.type, raw)
//   onUndo(turnId)
//   onOpenThread(threadId)

const UNDO_GRACE_MS = 5000;

function AgentLabel({ agentId }) {
  const a = AGENTS[agentId];
  if (!a) return null;
  return (
    <span className="agent-mark-row">
      <AgentMark agent={a} size="xs" />
      <span className="aname">{a.name.toUpperCase()}</span>
    </span>
  );
}

export default function AskCard({ ask, onAnswer, onUndo, onOpenThread, compact = false }) {
  const [whyOpen, setWhyOpen] = useState(false);
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState([]);
  // optimistic answered state: { choice } shown immediately on answer.
  const [optimistic, setOptimistic] = useState(null);
  const graceTimer = useRef(null);

  // Reset per-ask local state when this card slot is reused for a different ask,
  // and clear any pending grace timer on unmount (prevents stale-closure setState).
  useEffect(() => {
    setWhyOpen(false);
    setNote('');
    setPicked([]);
    setOptimistic(null);
    return () => {
      if (graceTimer.current) { clearTimeout(graceTimer.current); graceTimer.current = null; }
    };
  }, [ask.turnId]);

  const type = ask.type || 'fork';
  const agent = AGENTS[ask.agent];

  // already-answered (from server) — derive a display choice line.
  const serverAnswered = !!ask.answer;
  const answeredView = optimistic || (serverAnswered ? { choice: answerChoiceLine(type, ask.answer) } : null);

  function commit(raw, choiceLabel) {
    const payload = toAnswerPayload(type, raw);
    setOptimistic({ choice: choiceLabel, raw, payload });
    // fire the real answer; the parent owns the stream.
    if (onAnswer) onAnswer(ask.turnId, payload);
    if (graceTimer.current) clearTimeout(graceTimer.current);
    graceTimer.current = setTimeout(() => { graceTimer.current = null; }, UNDO_GRACE_MS);
  }

  function undo() {
    if (graceTimer.current) { clearTimeout(graceTimer.current); graceTimer.current = null; }
    setOptimistic(null);
    setNote('');
    setPicked([]);
    if (onUndo) onUndo(ask.turnId);
  }

  if (answeredView) {
    return (
      <div className={`ask-card answered ${compact ? 'compact' : ''}`}>
        <div className="ask-head">
          <AgentLabel agentId={ask.agent} />
          <span className="blocks answered-badge">ANSWERED</span>
        </div>
        {/* Restate the question, not just the answer, so the card reads in full. */}
        <p className="ask-q">{ask.q}</p>
        {ask.why && (
          <div className="ask-why">
            <button type="button" className="why-toggle" onClick={() => setWhyOpen(v => !v)}>
              {whyOpen ? '− Why it mattered' : '+ Why it mattered'}
            </button>
            {whyOpen && <p className="why-body">{ask.why}</p>}
          </div>
        )}
        <div className="ans-row">
          <span className="ans-label">Your answer</span>
          <span className="ans-choice">{answeredView.choice}</span>
        </div>
        <div className="ans-effect">
          {agent ? agent.name + ' is taking it from here.' : 'Taken from here.'}
          <button type="button" className="undo" onClick={undo}>Undo</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`ask-card ${compact ? 'compact' : ''}`}>
      <div className="ask-head">
        <AgentLabel agentId={ask.agent} />
        <span className={`blocks ${ask.tone || ''}`}>{ask.blocksLabel}</span>
      </div>
      <p className="ask-q">{ask.q}</p>
      {ask.why && (
        <div className="ask-why">
          <button type="button" className="why-toggle" onClick={() => setWhyOpen(v => !v)}>
            {whyOpen ? '− Why it matters' : '+ Why it matters'}
          </button>
          {whyOpen && <p className="why-body">{ask.why}</p>}
        </div>
      )}

      {type === 'fork' && (
        <>
          <div className="ask-answers">
            {ask.options.map(opt => (
              <button key={opt} type="button" className="opt"
                onClick={() => commit({ choice: opt }, opt)}>{opt}</button>
            ))}
          </div>
          <div className="ask-note">
            <input
              type="text"
              value={note}
              placeholder="Or answer in your own words…"
              onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && note.trim()) commit({ freeText: note.trim() }, '“' + note.trim() + '”'); }}
            />
            <button type="button" className="btn accent" disabled={!note.trim()}
              onClick={() => note.trim() && commit({ freeText: note.trim() }, '“' + note.trim() + '”')}>Send</button>
          </div>
        </>
      )}

      {type === 'review' && ask.work && (
        <>
          <div className="ask-work">
            {ask.work.title && <div className="aw-title">{ask.work.title}</div>}
            {(ask.work.paras || []).map((p, i) => <p key={i} className="aw-para">{p}</p>)}
          </div>
          <div className="ask-note">
            <input
              type="text"
              value={note}
              placeholder="Notes: what lands wrong, what must stay…"
              onChange={e => setNote(e.target.value)}
            />
          </div>
          <div className="ask-answers" style={{ paddingTop: 0 }}>
            <button type="button" className="opt approve"
              onClick={() => commit(
                { verdict: 'approve', notes: note.trim() },
                note.trim() ? 'Approved with notes: “' + note.trim() + '”' : 'Approved as drafted'
              )}>
              {note.trim() ? 'Approve with notes' : 'Approve as drafted'}
            </button>
            <button type="button" className="opt" disabled={!note.trim()}
              title={note.trim() ? '' : 'Add a note first. A redirect needs a direction.'}
              onClick={() => note.trim() && commit(
                { verdict: 'redirect', notes: note.trim() },
                'Redirected: “' + note.trim() + '”'
              )}>
              Redirect
            </button>
            <button type="button" className="opt reject"
              onClick={() => commit(
                { verdict: 'reject', notes: note.trim() },
                note.trim() ? 'Rejected: "' + note.trim() + '"' : 'Rejected'
              )}>
              Reject
            </button>
          </div>
        </>
      )}

      {type === 'pick' && (
        <>
          <div className="pick-grid">
            {ask.items.map(item => {
              const on = picked.includes(item.id);
              return (
                <button key={item.id} type="button" className={`pick-tile text ${on ? 'on' : ''}`}
                  onClick={() => setPicked(p => on ? p.filter(x => x !== item.id) : [...p, item.id])}>
                  <div className="pt-head">
                    {item.kind && <span className="rm-kind">{item.kind}</span>}
                    {on && <span className="pick-check">KEPT</span>}
                  </div>
                  {item.short && <div className="pt-short">{item.short}</div>}
                  {item.cap && <div className="rm-cap">{item.cap}</div>}
                </button>
              );
            })}
          </div>
          <div className="ask-answers" style={{ paddingTop: 4 }}>
            <button type="button" className="opt approve" disabled={picked.length === 0}
              onClick={() => {
                const kept = ask.items.filter(i => picked.includes(i.id));
                const shorts = kept.map(i => i.short);
                commit(
                  { keptItemIds: kept.map(i => i.id) },
                  'Kept ' + shorts.length + ' of ' + ask.items.length + ': ' + shorts.join(' · ')
                );
              }}>
              Keep {picked.length || '…'} selected
            </button>
            <button type="button" className="opt"
              onClick={() => commit({ none: true }, 'Kept none, pull a fresh set')}>
              None of these, pull fresh
            </button>
          </div>
        </>
      )}

      {type === 'unblock' && (
        <>
          <div className="ask-answers">
            {ask.options.map(qk => (
              <button key={qk} type="button" className="opt"
                onClick={() => commit({ quickChoice: qk }, qk)}>{qk}</button>
            ))}
          </div>
          <div className="ask-note">
            <input
              type="text"
              value={note}
              placeholder="Only you know this one…"
              onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && note.trim()) commit({ freeText: note.trim() }, '“' + note.trim() + '”'); }}
            />
            <button type="button" className="btn accent" disabled={!note.trim()}
              onClick={() => note.trim() && commit({ freeText: note.trim() }, '“' + note.trim() + '”')}>Send</button>
          </div>
        </>
      )}

      <div className="ask-foot">
        <button type="button" className="thread-link" onClick={() => onOpenThread && onOpenThread(ask.threadId)}>
          Open the thread → how {agent ? agent.name : 'the agent'} got here
        </button>
      </div>
    </div>
  );
}

// Render a short human label for an already-answered ask payload.
function answerChoiceLine(type, answer) {
  const p = answer || {};
  switch (type) {
    case 'fork':
      return p.freeText ? '“' + p.freeText + '”' : (p.choice || 'Answered');
    case 'review':
      if (p.verdict === 'reject') return p.notes ? 'Rejected: “' + p.notes + '”' : 'Rejected';
      if (p.verdict === 'redirect') return p.notes ? 'Redirected: “' + p.notes + '”' : 'Redirected';
      return p.notes ? 'Approved with notes: “' + p.notes + '”' : 'Approved as drafted';
    case 'pick':
      if (p.none) return 'Kept none, pull a fresh set';
      return 'Kept ' + ((p.keptItemIds || []).length) + ' selected';
    case 'unblock':
      return p.quickChoice ? p.quickChoice : (p.freeText ? '“' + p.freeText + '”' : 'Answered');
    default:
      return 'Answered';
  }
}
